import { formatDate, humanizeCount, formatCurrency, checkFulfillmentCanFileClaim } from '../../../helpers';

import { BaseModel } from './base.model';
import { OrderFulfillmentModel, OrderActivityModel, OrderMessageModel, OrderItemModel, ClaimModel } from './index';

export class OrderModel extends BaseModel<any> {
  private claimMessages: any[] = [];

  // TODO: add uniqBy to just filter the messages by id
  loadMessages(messages: any[], opts?: { replace?: boolean }) {
    if (messages?.length > 0) {
      this.claimMessages = opts?.replace ? messages : this.claimMessages.concat(messages);
    }
  }

  addMessage(message: any) {
    this.claimMessages.push(message);
  }

  set data(data: any) {
    this._data = data;
  }

  get subtotal() {
    return this._data.subtotal;
  }

  get id() {
    return this._data.id;
  }

  get deletedAt() {
    return this._data.deletedAt;
  }

  get sourceCancelledAt() {
    return this._data.sourceCancelledAt;
  }

  get eligibleForClaim() {
    return this._data.policyId || this._data.eligibleForOta === 'ELIGIBLE' || !!this._data.warranties;
  }

  get eligibleForOta() {
    return (
      (this._data.policyId && this._data.eligibleForOta === 'IS_OTA') ||
      (!this._data.policyId && this._data.eligibleForOta === 'ELIGIBLE')
    );
  }

  get printableId() {
    return this._data.sourceOrderNumber;
  }

  get customer() {
    return this._data.customerName;
  }

  setMessages(data: any[]) {
    this.claimMessages = data;
  }

  setPolicy(data: any) {
    this._data = { ...this._data, policy: data };
  }

  setWarranties(data: any) {
    this._data = { ...this._data, warranties: data };
  }

  setHasChargeback(hasChargeback: boolean) {
    this._data = { ...this._data, hasChargeback };
  }

  setOrderActivities(activities: any[]) {
    this._data = { ...this._data, orderActivities: activities };
  }

  setDiscountsUsed(discounts: any[]) {
    this._data = { ...this._data, discountsUsed: discounts };
  }

  get policy() {
    return this._data.policy;
  }

  get customerData() {
    return {
      id: this._data.customerId,
      name: this._data.customerName,
      email: this._data.customerEmail,
      phone: this._data.customerPhone,
      ip: this._data.customerIp,
    };
  }

  get date() {
    return formatDate(this._data.createdAt);
  }

  get sourceOrderId() {
    return this._data.sourceOrderId;
  }

  get originalSourceOrderId() {
    return this._data.originalSourceOrderId;
  }

  get originalOrderId() {
    return this._data.originalOrderId;
  }

  get hasChargeback() {
    return this._data.hasChargeback;
  }

  get data() {
    return this._data;
  }

  get platformId() {
    return this._data.platformId;
  }

  get shippingAddressChange() {
    const shippingAddresses = this._data.address.filter(address => address.type === 'SHIPPING');

    return shippingAddresses.length > 1 ? 'Yes' : 'No';
  }

  get shippingAddressHistory() {
    return this._data.address
      ?.filter(address => address.type === 'SHIPPING')
      .sort((a, b) => new Date(a.createdAtTime).getTime() - new Date(b.createdAtTime).getTime());
  }

  get url() {
    return `/claims/view/${this.id}`;
  }

  get itemsCount() {
    return this._data?.orderItems?.length || this._data._count?.orderItems || 0;
  }

  get itemsCountHumanized() {
    return humanizeCount(this.itemsCount, 'item', 'items');
  }

  get premiumPaid() {
    return this._data.premiumPaid;
  }

  get address() {
    return this._data.address;
  }

  get customerName() {
    return this._data.customerName;
  }

  get customerPhone() {
    return this._data.customerPhone;
  }

  get customerEmail() {
    return this._data.customerEmail;
  }

  get orderItems() {
    return this._data.orderItems;
  }

  get shippingCost() {
    return this._data.shippingCost ?? 0;
  }

  get shippingCostTax() {
    return this._data.shippingCostTax;
  }

  get tax() {
    return this._data.tax ?? 0;
  }

  get currency() {
    return this._data.currency;
  }

  get total() {
    return this._data.total;
  }

  get inventory() {
    return this._data.inventory ?? [];
  }

  get warranties() {
    return this._data.warranties ?? [];
  }

  setInventory(inventory: any) {
    this._data = { ...this._data, inventory };
  }

  get exchangeRate() {
    return this._data.exchangeRate;
  }

  get payment() {
    let conversion: { currency: any; price: number; priceInDollars: number } | null = null;
    if (this._data.exchangeRate !== 1) {
      conversion = {
        currency: this._data.currency,
        price: +this._data.total.toFixed(2),
        priceInDollars: this._data.total * this._data.exchangeRate,
      };
    }
    const premiumPaid = this._data?.policy?.premiumPaid ?? 0;
    const premiumCost = this._data?.premiumCost ?? 0;
    const warrantyPremium = this._data?.warranties?.customerAmount ?? this._data?.warranties?.premiumPaid ?? 0;
    return {
      conversion,
      currency: this._data.currency,
      lineItems: this.products,
      protection: {
        paidPrice: +premiumPaid.toFixed(2),
        price: +premiumCost.toFixed(2),
      },
      warranty: {
        price: +warrantyPremium.toFixed(2),
      },
      shipping: {
        currency: this._data.currency,
        price: +this.shippingCost.toFixed(2),
        tax: Number(this._data.shippingCostTax ?? 0).toFixed(2),
        title: '',
      },
      tax: {
        // percent: '15%',
        currency: this._data.currency,
        price: +this.tax.toFixed(2),
      },
      total: {
        price: formatCurrency(this._data.total, this._data.currency),
      },
      discountsUsed: this._data.discountsUsed,
    };
  }

  get fulfillments() {
    const fulfillments = OrderFulfillmentModel.fromArrayExtending(this._data.fulfillments, {
      order: this._data,
    }) as OrderFulfillmentModel[];

    return fulfillments.filter(fulfillment => !fulfillment.isDeleted);
  }

  get storeId() {
    return this._data.storeId;
  }

  get reshipments() {
    return this._data.reshipments;
  }

  get products() {
    return OrderItemModel.fromArrayExtending(this._data.orderItems, {
      currency: this.currency,
      order: this,
    });
  }

  get messages() {
    return OrderMessageModel.fromArray(this.claimMessages || this.messagesTemplate);
  }

  get activities() {
    const activities = this._data.activities;
    const orderActivities =
      this?._data?.orderActivities?.map(activity => {
        const { id, source, sourceType, activityType, description, createdAt, imageUrl } = activity.node;

        return {
          activityType,
          createdAt,
          cursor: '',
          id,
          imageUrl,
          message: description,
          source,
          sourceType,
        };
      }) ?? [];

    return OrderActivityModel.fromArray(activities.concat(orderActivities));
  }

  set activities(activities: any[]) {
    this._data.activites = activities;
  }

  get allMessagesAndActivities() {
    return [...this.messages, ...this.activities].sort((a: any, b: any) => {
      return a.createdAtTime - b.createdAtTime;
    });
  }

  private get messagesTemplate() {
    return [
      {
        createdAt: '2021-08-01T12:00:00Z',
        image: '/headshots/john.jpeg',
        medias: [{ caption: '', fileKey: '/headshots/john.jpeg' }],
        message: `
    Hi Allison,

    Thank you for contacting us. We're happy to help get resolved wtih this. I can see that you ordered Immune revival (Stick Packs: Unflavored), may I know how many packets are damaged?

    Thanks!
    `,
        messageUserType: 'system',
        type: 'message',
        username: 'Raul Clavecillas',
      },
      {
        createdAt: '2021-08-01T12:30:00Z',
        message: `It's hard to tell because the packets are so thin. I've tried to open a few of them but there is hardly anything that comes out of one and then the next one is fine.`,
        messageUserType: 'user',
        type: 'message',
        username: 'Stacey Renza',
      },
      {
        createdAt: '2021-08-01T13:00:00Z',
        image: '/headshots/john.jpeg',
        medias: [{ caption: '', fileKey: '/headshots/john.jpeg' }],
        message: `
    Thank you for your responses to us. Can you tell if the product is damaged during the shipment? Is the box of the parcel damaged as well?

    Thanks!
    `,
        messageUserType: 'system',
        type: 'message',
        username: 'Raul Clavecillas',
      },
      {
        createdAt: '2021-08-01T13:30:00Z',
        message: `Yes the parcel was smushed therefore causing the product to be damaged. I sent in pictures of the box before I opened it as well as the box after opening it`,
        messageUserType: 'user',
        type: 'message',
        username: 'Stacey Renza',
      },
      {
        createdAt: '2021-08-01T14:00:00Z',
        image: '/headshots/john.jpeg',
        medias: [{ caption: '', fileKey: '/headshots/john.jpeg' }],
        message: `
Hi Allison,

We're sorry about the damaged item. I have shipped your replacement order.

It will be sent using the same shipping method you originally purchased.

You will receive an order confirmation shortly and a shipping confirmation as soon as the order goes out. Have a great day!
`,
        messageUserType: 'system',
        type: 'message',
        username: 'Raul Clavecillas',
      },
    ];
  }
}
